import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Target, TrendingUp, Award, DollarSign, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const TargetAchievement = ({ user }) => {
  const [achievements, setAchievements] = useState([]);
  const [allAchievements, setAllAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedRep, setSelectedRep] = useState('all');

  const months = [
    { value: '1', label: 'Ocak' },
    { value: '2', label: 'Şubat' },
    { value: '3', label: 'Mart' },
    { value: '4', label: 'Nisan' },
    { value: '5', label: 'Mayıs' },
    { value: '6', label: 'Haziran' },
    { value: '7', label: 'Temmuz' },
    { value: '8', label: 'Ağustos' },
    { value: '9', label: 'Eylül' },
    { value: '10', label: 'Ekim' },
    { value: '11', label: 'Kasım' },
    { value: '12', label: 'Aralık' }
  ];

  const years = ['2024', '2025', '2026'];

  useEffect(() => {
    loadAchievements();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    // Temsilci filtresi değiştiğinde filtreleme uygula
    if (selectedRep === 'all') {
      setAchievements(allAchievements);
    } else {
      setAchievements(allAchievements.filter(item => item.username === selectedRep));
    }
  }, [selectedRep, allAchievements]);

  const loadAchievements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE}/api/target-achievements?token=${token}&month=${selectedMonth}&year=${selectedYear}`
      );
      setAllAchievements(response.data);
      
      // Filtreleme uygula
      if (selectedRep === 'all') {
        setAchievements(response.data);
      } else {
        setAchievements(response.data.filter(item => item.username === selectedRep));
      }
    } catch (error) {
      console.error('Error loading achievements:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getAchievementColor = (rate) => {
    if (rate >= 180) return 'from-green-600 to-emerald-600';
    if (rate >= 120) return 'from-green-500 to-emerald-500';
    if (rate >= 100) return 'from-lime-500 to-green-500';
    return 'from-red-500 to-rose-500';
  };

  const getAchievementBadge = (rate) => {
    if (rate >= 180) return { text: 'Mükemmel', color: 'bg-green-100 text-green-800' };
    if (rate >= 120) return { text: 'Çok İyi', color: 'bg-emerald-100 text-emerald-800' };
    if (rate >= 100) return { text: 'İyi', color: 'bg-lime-100 text-lime-800' };
    return { text: 'Geliştirilmeli', color: 'bg-red-100 text-red-800' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('tr-TR').format(num);
  };

  // Toplam istatistikler
  const totalStats = achievements.reduce((acc, item) => {
    if (item.has_target) {
      acc.totalTarget += item.target_points;
      acc.totalAchieved += item.achieved_points;
      acc.totalCommission += item.commission;
      acc.count += 1;
    }
    return acc;
  }, { totalTarget: 0, totalAchieved: 0, totalCommission: 0, count: 0 });

  const overallRate = totalStats.totalTarget > 0 
    ? (totalStats.totalAchieved / totalStats.totalTarget * 100) 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Target className="w-8 h-8" />
            Hedef Gerçekleştirme
          </h1>
          <p className="text-gray-600 mt-1">Aylık hedef ve hakediş takibi</p>
        </div>
        
        {/* Dönem Seçimi ve Temsilci Filtresi */}
        <div className="flex gap-3">
          {user?.role === 'admin' && (
            <Select value={selectedRep} onValueChange={setSelectedRep}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Temsilci Seç" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Temsilciler</SelectItem>
                {allAchievements
                  .filter(item => item.has_target)
                  .map(item => (
                    <SelectItem key={item.username} value={item.username}>
                      {item.full_name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
          
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map(month => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(year => (
                <SelectItem key={year} value={year}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Genel İstatistikler - Admin için */}
      {user?.role === 'admin' && totalStats.count > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Toplam Hedef</p>
                <p className="text-2xl font-bold mt-1">{formatNumber(totalStats.totalTarget)}</p>
                <p className="text-blue-100 text-xs mt-1">Puan</p>
              </div>
              <Target className="w-12 h-12 text-blue-200" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Toplam Gerçekleşen</p>
                <p className="text-2xl font-bold mt-1">{formatNumber(totalStats.totalAchieved)}</p>
                <p className="text-green-100 text-xs mt-1">Puan</p>
              </div>
              <TrendingUp className="w-12 h-12 text-green-200" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Ortalama Gerçekleşme</p>
                <p className="text-2xl font-bold mt-1">{overallRate.toFixed(1)}%</p>
                <p className="text-purple-100 text-xs mt-1">Hedef Oranı</p>
              </div>
              <Award className="w-12 h-12 text-purple-200" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Toplam Hakediş</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totalStats.totalCommission)}</p>
                <p className="text-orange-100 text-xs mt-1">{totalStats.count} Temsilci</p>
              </div>
              <DollarSign className="w-12 h-12 text-orange-200" />
            </div>
          </Card>
        </div>
      )}

      {/* Temsilci Kartları */}
      <div className={`grid gap-4 lg:gap-6 ${
        user?.role === 'admin' 
          ? 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3' 
          : 'grid-cols-1 max-w-4xl mx-auto'
      }`}>
        {achievements.map((achievement, index) => {
          if (!achievement.has_target) return null;
          
          const badge = getAchievementBadge(achievement.achievement_rate);
          const progressColor = getAchievementColor(achievement.achievement_rate);
          const progressWidth = Math.min(achievement.achievement_rate, 200); // Max %200 göster

          return (
            <Card key={index} className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              {/* Header */}
              <div className={`bg-gradient-to-r ${progressColor} p-6 text-white`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold">{achievement.full_name}</h3>
                    <p className="text-sm opacity-90 mt-1">@{achievement.username}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
                    {badge.text}
                  </span>
                </div>
                
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span>Gerçekleşme Oranı</span>
                    <span className="font-bold text-lg">{achievement.achievement_rate.toFixed(1)}%</span>
                  </div>
                  <div className="bg-white bg-opacity-30 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-white h-full rounded-full transition-all duration-500"
                      style={{ width: `${progressWidth}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                {/* Hedef vs Gerçekleşen */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-600 mb-2">
                      <Target className="w-4 h-4" />
                      <span className="text-xs font-medium">HEDEF</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">
                      {formatNumber(achievement.target_points)}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">Puan</p>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-green-600 mb-2">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-xs font-medium">GERÇEKLEŞEN</span>
                    </div>
                    <p className="text-2xl font-bold text-green-900">
                      {formatNumber(achievement.achieved_points)}
                    </p>
                    <p className="text-xs text-green-600 mt-1">Puan</p>
                  </div>
                </div>

                {/* Hakediş */}
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-5 rounded-lg border-2 border-orange-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-500 p-3 rounded-full">
                        <DollarSign className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Hakediş</p>
                        <p className="text-3xl font-bold text-orange-600">
                          {formatCurrency(achievement.commission)}
                        </p>
                      </div>
                    </div>
                    {achievement.achievement_rate >= 180 ? (
                      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                        EXTRA BONUSLU PRİM
                      </div>
                    ) : achievement.achievement_rate >= 120 ? (
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                        BONUSLU PRİM
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Teşvik Mesajı */}
                {achievement.achievement_rate >= 180 ? (
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-4 rounded-lg text-center">
                    <p className="text-lg font-bold">✓ Üstün Performans</p>
                    <p className="text-sm mt-1">Extra Bonuslu Prim hak edildi</p>
                  </div>
                ) : achievement.achievement_rate >= 120 ? (
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-4 rounded-lg">
                    <p className="text-xs font-semibold mb-1">📊 Hedef İlerleme Durumu</p>
                    <p className="text-base font-bold">%180 Hedefine Yaklaştınız</p>
                    <div className="mt-2 bg-white bg-opacity-20 rounded p-2">
                      <p className="text-xs">Extra Bonuslu Prim için kalan: <span className="font-bold">
                        {((achievement.target_points * 1.8) - achievement.achieved_points).toFixed(0)} puan
                      </span></p>
                    </div>
                  </div>
                ) : achievement.achievement_rate >= 100 ? (
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-4 rounded-lg">
                    <p className="text-xs font-semibold mb-1">📊 Hedef İlerleme Durumu</p>
                    <p className="text-base font-bold">%120 Hedefine Yaklaştınız</p>
                    <div className="mt-2 bg-white bg-opacity-20 rounded p-2">
                      <p className="text-xs">Bonuslu Prim için kalan: <span className="font-bold">
                        {((achievement.target_points * 1.2) - achievement.achieved_points).toFixed(0)} puan
                      </span></p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-4 rounded-lg">
                    <p className="text-xs font-semibold mb-1">📊 Hedef Durumu</p>
                    <p className="text-base font-bold">%100 Hedefe Doğru İlerleme</p>
                    <div className="mt-2 bg-white bg-opacity-20 rounded p-2">
                      <p className="text-xs">Hedef tamamlanması için kalan: <span className="font-bold">
                        {(achievement.target_points - achievement.achieved_points).toFixed(0)} puan
                      </span></p>
                    </div>
                  </div>
                )}
                
                {/* Bilgi Kutusu */}
                <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600">
                  <p className="font-semibold mb-1">Hakediş Hesaplama:</p>
                  {achievement.achievement_rate >= 180 ? (
                    <p>🎉 %180+ → Puan × 0.25 + 7.500 TL + EXTRA BONUSLU PRİM</p>
                  ) : achievement.achievement_rate >= 120 ? (
                    <p>⭐ %120+ → Puan × 0.25 + 2.500 TL + BONUSLU PRİM</p>
                  ) : achievement.achievement_rate >= 100 ? (
                    <p>👍 %100+ → Puan × 0.15</p>
                  ) : (
                    <p>❌ %100 altı → Hakediş yok</p>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Hedef Olmayan Kullanıcılar Uyarısı */}
      {achievements.filter(a => !a.has_target).length > 0 && user?.role === 'admin' && (
        <Card className="p-6 bg-yellow-50 border-yellow-200">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-yellow-600 mt-1" />
            <div>
              <h3 className="font-semibold text-yellow-900">Hedef Tanımlanmamış Kullanıcılar</h3>
              <p className="text-sm text-yellow-700 mt-1">
                {achievements.filter(a => !a.has_target).map(a => a.full_name).join(', ')} 
                {' '}için bu ay hedef tanımlanmamış. 
                <a href="/targets" className="underline ml-1 font-medium">Hedefler</a> sayfasından ekleyebilirsiniz.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Veri Yoksa */}
      {achievements.length === 0 && (
        <Card className="p-12 text-center">
          <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Henüz Hedef Tanımlanmamış</h3>
          <p className="text-gray-500">
            {user?.role === 'admin' 
              ? 'Hedefler sayfasından temsilcilere hedef tanımlayabilirsiniz.'
              : 'Yöneticiniz size henüz hedef tanımlamamış.'}
          </p>
        </Card>
      )}
    </div>
  );
};

export default TargetAchievement;
